import { EventEntity } from "@citychallenge/api-public";
import { LocalizedError } from "../../errors";
import { EventModel } from "../../models";
import { ApiService } from "../api";
import { UserService } from "../user";
import { computed, observable, action } from "mobx";

import { DivisionService, divisionTokens } from "../division";
import { ServiceInterface } from "../service.interface";
import { createContext } from "react";
import { Loadable } from "../../loadable";

export const EventServiceContext = createContext<EventService>(null as any);

export class EventService implements ServiceInterface {
  public context = EventServiceContext;

  constructor(
    private apiService: ApiService,
    private userService: UserService,
    private divisionService: DivisionService
  ) {}

  @observable
  private _currentlyEditing = EventModel.create();

  public serviceWillBoot() {
    this.resetCurrentlyEditing();
  }

  private _createEditingModel() {
    return EventModel.create({
      phone: this.userService.user?.phone,
    });
  }

  @computed
  public get currentlyEditing(): Partial<EventModel> {
    return this._currentlyEditing;
  }

  @action
  public resetCurrentlyEditing = () => {
    this._currentlyEditing = this._createEditingModel();
  };

  public create = async (eventModel: Partial<EventModel>) => {
    const { division } = this;

    const validated = await eventModel.getValidatedObject?.();
    if (!validated) throw new LocalizedError();

    const { phone } = eventModel;

    // Update phone number in user account when it has changed
    if (phone && phone !== this.userService.user?.phone)
      await this.userService.updateUser(
        {
          phone: phone,
        },
        false
      );

    const { data } = await this.apiService.clientPrivate.clientCreateEvent(
      division.id,
      validated
    );

    return this.insertEventEntity(data);
  };

  public update = async (eventId: string, eventModel: Partial<EventModel>) => {
    const validated = await eventModel.getValidatedObject?.();
    if (!validated) throw new LocalizedError();

    return this.apiService.clientPrivate.clientUpdateEvent(eventId, validated);
  };

  public insertEventEntity(entity: EventEntity) {
    const { division } = this;
    const model = new EventModel(entity, this.userService);
    division.eventStore.addEvent(model);
    return model;
  }

  public get division() {
    const division = this.divisionService.activeDisplayDivision;
    if (!division)
      throw new LocalizedError(undefined, divisionTokens.noActiveDivision);
    return division;
  }

  public fetchDivisionEvents = async (divisionId: string) => {
    const { data } = await this.apiService.clientPublic.eventsForDivision(
      divisionId
    );

    return data.map((event) => {
      return new EventModel(event, this.userService);
    });
  };

  public loadableEventStore = () => {
    const { division } = this;

    if (division.loadableEventStore) return division.loadableEventStore;

    division.loadableEventStore = Loadable.create(
      async () => {
        const { eventStore } = division;
        const events = await this.fetchDivisionEvents(division.id);
        eventStore.setEvents(events);

        return eventStore;
      },
      {
        translatedContent: true,
        refreshInterval: 15 * 60, // Refresh
      }
    );

    return division.loadableEventStore;
  };

  public async loadRemoteEvent(eventId: string) {
    const { division } = this;

    const { data } = await this.apiService.clientPublic.getEventInDivision(
      division.id,
      eventId
    );

    const model = this.insertEventEntity(data);

    return model;
  }

  public async findSingleEvent(eventId: string) {
    const { ensuredValue } = this.loadableEventStore();
    const store = await ensuredValue();
    const value = store.findById(eventId);
    if (!value) {
      return this.loadRemoteEvent(eventId);
    }
    return value;
  }

  public loadableEvent = (eventId: string) => {
    return Loadable.create(async () => {
      return this.findSingleEvent(eventId);
    });
  };

  /**
   * Ensure that the participants property is loaded in the EventModel
   */
  public ensureParticipants(event: EventModel) {
    if (event.participantsLoadable) return event.participantsLoadable;

    event.participantsLoadable = Loadable.create(async () => {
      const { data } = await this.apiService.clientPublic.participantsForEvent(
        event.id
      );

      event.setParticipants(data);
      return event.participants;
    });

    return event.participantsLoadable;
  }

  public async participate(event: EventModel) {
    const { data } = await this.apiService.clientPrivate.clientParticipateEvent(
      event.id
    );

    event.setParticipants(data);

    return event;
  }

  public async unparticipate(event: EventModel) {
    const { data } =
      await this.apiService.clientPrivate.clientUnparticipateEvent(event.id);

    event.setParticipants(data);
    return event;
  }
}
